import React from 'react'
import { Box, LinearProgress, Typography, useTheme } from '@mui/material'

import { usePasswordStrength, PasswordStrengthLabel } from 'hooks/usePasswordStrength'

// VERA-837: below PASSWORD_STRENGTH_THRESHOLD (score 3), the label must read
// as explicitly NOT acceptable. "Fair" on its own reads as "probably fine",
// which is exactly what caused the "no matter what I type I get the same
// error" report (users typed a score-1/2 password, saw a plausible-looking
// label, submitted, and got rejected by the server with no idea why). At/above
// threshold gets an equally explicit pass state.
const STATUS_TEXT: Record<PasswordStrengthLabel, string> = {
  Weak: 'Weak. Not strong enough yet.',
  Fair: 'Fair. Not strong enough yet.',
  Good: 'Good. Strong enough.',
  Strong: 'Strong password.',
}

// VERA-837: zxcvbn's own `warning`/`suggestion` strings are written for a
// library consumer, not a user — e.g. "This is similar to a commonly used
// password" reads as an internal debug string, not an instruction. Below
// PASSWORD_STRENGTH_THRESHOLD (score 3), `label` is only ever 'Weak' (score
// 0-1) or 'Fair' (score 2), so a static, actionable line per band covers
// every below-threshold case. We deliberately do NOT cherry-pick "the good
// zxcvbn warnings" and fall back to canned text for the rest: a meter that's
// sometimes a library string and sometimes plain English reads as
// inconsistent, and none of zxcvbn's warning strings are reliably actionable
// across the whole Weak/Fair range (many are just "match found" internals).
// One static line per band keeps the copy predictable and on-brand.
const GUIDANCE_TEXT: Partial<Record<PasswordStrengthLabel, string>> = {
  Weak: 'Try something longer and less common.',
  Fair: 'Try something stronger.',
}

const statusColor = (theme: any, label: PasswordStrengthLabel | null, meetsThreshold: boolean) => {
  if (!label) return theme.palette.text.secondary
  if (meetsThreshold) return theme.palette.success.main
  return label === 'Weak' ? theme.palette.error.main : theme.palette.warning.main
}

interface PasswordStrengthMeterProps {
  password: string
  sx?: object
}

/**
 * VERA-837: live client-side password-strength feedback, mirroring the
 * zxcvbn-style scoring WorkOS applies server-side (see usePasswordStrength
 * for the threshold + how it was determined).
 *
 * The bar is always present (so the control never pops in), but the text
 * COLLAPSES to zero height when there is nothing to say and animates open
 * when there is.
 */
export const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({ password, sx }) => {
  const theme = useTheme()
  const { score, label, meetsThreshold, isLoading } = usePasswordStrength(password)

  const touched = password.length > 0
  // Continuous 0-100 fill (score is 0-4, i.e. 5 levels) rather than 4
  // discrete segments: a single rounded track that grows smoothly.
  const progress = touched && score !== null ? ((score + 1) / 5) * 100 : 0
  const trackColor = touched ? statusColor(theme, label, meetsThreshold) : theme.palette.grey[300]

  const statusText = isLoading && label === null ? 'Checking...' : label ? STATUS_TEXT[label] : ''
  const guidanceText = label && !meetsThreshold ? GUIDANCE_TEXT[label] || '' : ''
  const hasText = Boolean(statusText || guidanceText)

  // NOTE: apps/app's theme uses an 8px spacing base (NOT the 4px base in
  // packages/ui), so mt:2 here was 16px and stacked on the field's own 16px
  // mb for a 32px gap. The field already provides the gap above, so this
  // block adds none of its own on top.
  return (
    <Box sx={{ mt: 0, mb: 2, ...sx }}>
      <LinearProgress
        variant='determinate'
        value={progress}
        sx={{
          height: 4,
          borderRadius: '999px',
          backgroundColor: theme.palette.grey[200],
          '& .MuiLinearProgress-bar': {
            borderRadius: '999px',
            backgroundColor: trackColor,
            transition: 'transform 200ms ease, background-color 200ms ease',
          },
        }}
      />
      {/*
        VERA-837: an earlier pass reserved a FIXED height for these lines to
        stop the button jumping. That killed the jump but left a permanent
        dead gap under the bar even on an untouched field. Instead, collapse
        to zero and animate open: the grid 0fr -> 1fr trick animates intrinsic
        height, which a plain `height: auto` transition cannot. The button
        glides rather than either jumping or being pushed down by empty space.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: hasText ? '1fr' : '0fr',
          transition: 'grid-template-rows 220ms ease',
        }}
      >
        <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
          <Typography
            component='p'
            variant='body3'
            sx={{
              mt: 2,
              color: isLoading && label === null ? theme.palette.text.secondary : trackColor,
              lineHeight: 1.4,
            }}
          >
            {statusText}
          </Typography>
          {guidanceText ? (
            <Typography
              component='p'
              variant='body3'
              sx={{ color: theme.palette.text.secondary, mt: 1, lineHeight: 1.4 }}
            >
              {guidanceText}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}
